import { Button, type LayoutProps } from '@chakra-ui/react'
import { faCheck, faPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import TutorFollowQ from '@/queries/TutorFollowQ'
import UserQ from '@/queries/UserQ'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import type { Query } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'

import RequireLoginAndApproval from '@/components/Auth/RequireLoginAndApproval'

export default function TutorFollowButton({
  tutor,
  width = 120,
  followText = 'Follow',
  followingText = 'Following',
}: {
  tutor: Query
  width?: LayoutProps['width']
  followText?: string
  followingText?: string
}): JSX.Element | null {
  const user = useUser()
  const tutorUserId = tutor(UserQ.id) ?? 0
  const isCurrentUser = tutorUserId === user.id
  const isFollowing =
    (tutor`EXISTS(SELECT 1 FROM ${TutorFollowQ} WHERE
      ${TutorFollowQ.tutor_id} = ${UserQ.id} AND ${TutorFollowQ.user_id} = ${user.id})` as
      | boolean
      | null) ?? false

  const [handleFollowTutor, isFollowingLoading] = useAPIWrapper(
    async () => {
      await RPCRefresh.followTutor({ tutorUserId })
    },
    [tutorUserId],
    'Follow Tutor'
  )

  const [handleUnfollowTutor, isUnfollowing] = useAPIWrapper(
    async () => {
      await RPCRefresh.unfollowTutor({ tutorUserId })
    },
    [tutorUserId],
    'Unfollow Tutor'
  )

  if (isCurrentUser) {
    return null
  }

  return (
    <RequireLoginAndApproval>
      {isFollowing ? (
        <Button
          width={width}
          variant="outline"
          isLoading={isUnfollowing}
          onClick={handleUnfollowTutor}
          leftIcon={<FontAwesomeIcon icon={faCheck} />}
        >
          {followingText}
        </Button>
      ) : (
        <Button
          width={width}
          isLoading={isFollowingLoading}
          onClick={handleFollowTutor}
          leftIcon={<FontAwesomeIcon icon={faPlus} />}
        >
          {followText}
        </Button>
      )}
    </RequireLoginAndApproval>
  )
}
